import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import cn from 'classnames';
import { useTheme } from '../../theme';
import Dropdown from '../../Dropdown/Dropdown';
import Input from '../../Input';
const Autocomplete = (props) => {
    const { className, dropDownClassName, isLoading, options, inputValue, inputRenderer, groupRenderer, optionRenderer, dropdownRenderer, onInputChange, onSelect, callOnSelectOn, onClickOutsideProps, label, endAdornment, error, name, onBlur, onFocus, innerRef, disabled, variant, autofocus } = props;
    const { Autocomplete: css } = useTheme('Autocomplete');
    const [isOpen, setIsOpen] = useState(false);
    const handleFocus = useCallback((event) => {
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
        setIsOpen(true);
    }, [onFocus]);
    const handleBlur = useCallback((event) => {
        setTimeout(() => {
            setIsOpen(false);
        }, 200);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    }, [onBlur]);
    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);
    const getOnSelect = useCallback((item) => () => {
        onSelect(item);
        handleClose();
    }, [onSelect, handleClose]);
    const renderOption = useCallback((option, key) => {
        // Custom renderer.
        if (optionRenderer) {
            return optionRenderer(option, key, getOnSelect(option.value));
        }
        if (callOnSelectOn === 'mouseDown') {
            return (React.createElement(MenuItem, { key: key, className: css.option, onMouseDown: getOnSelect(option.value) }, option.label));
        }
        return (React.createElement(MenuItem, { key: key, className: css.option, onClick: getOnSelect(option.value) }, option.label));
    }, [optionRenderer, callOnSelectOn, getOnSelect]);
    const renderGroup = useCallback((group, key) => {
        // Custom renderer.
        if (groupRenderer) {
            return groupRenderer(group, key, renderOption);
        }
        return (!!group.options.length && (React.createElement("div", { key: key, className: css.group },
            React.createElement("div", { className: css.group__label }, group.label),
            React.createElement("div", { className: css.group__options }, group.options.map(renderOption)))));
    }, [groupRenderer, renderOption]);
    const renderInput = useMemo(() => {
        const inputProps = {
            type: 'text',
            className: css.input,
            value: inputValue,
            onChange: onInputChange,
            onFocus: handleFocus,
            onBlur: handleBlur,
            spellCheck: false,
            autoComplete: 'off',
            autoCorrect: 'off'
        };
        // Custom renderer.
        if (inputRenderer) {
            return inputRenderer(isOpen, isLoading, inputProps);
        }
        return (React.createElement(Input, { label: label, value: inputValue, name: name || '', className: css.input, autoFocus: autofocus, disabled: disabled, variant: variant, TextFieldProps: {
                error: !!error,
                helperText: error,
                onBlur: handleBlur,
                onFocus: handleFocus,
                autoComplete: inputProps.autoComplete,
                InputProps: {
                    onChange: onInputChange,
                    endAdornment: endAdornment || '',
                    autoComplete: inputProps.autoComplete,
                    className: css.inner_input
                }
            }, absoluteHintPosition: true }));
    }, [
        inputValue,
        onInputChange,
        handleFocus,
        handleBlur,
        inputRenderer,
        isOpen,
        isLoading,
        label,
        name,
        autofocus,
        disabled,
        variant,
        error,
        endAdornment
    ]);
    const renderDropdown = useMemo(() => {
        // Custom renderer.
        if (dropdownRenderer) {
            return dropdownRenderer({
                options,
                isOpen,
                onClose: handleClose,
                onInputChange,
                onSelect,
                renderGroup,
                renderOption
            });
        }
        return (React.createElement(Dropdown, Object.assign({}, onClickOutsideProps, { isOpen: options.length > 0 && isOpen, onClose: handleClose }),
            React.createElement(Paper, { className: cn(css.options, dropDownClassName), "data-test-id": "options" }, options.map((item, index) => 'value' in item ? renderOption(item, index) : renderGroup(item, index)))));
    }, [
        dropdownRenderer,
        options,
        isOpen,
        handleClose,
        onInputChange,
        onSelect,
        renderGroup,
        renderOption,
        onClickOutsideProps,
        dropDownClassName
    ]);
    return (React.createElement("div", { ref: innerRef, className: cn(css.autocomplete, className) },
        renderInput,
        renderDropdown));
};
Autocomplete.displayName = 'Autocomplete';
export default Autocomplete;
